import React, { useState, useMemo, useEffect } from 'react';
import { get_all_withdraw_request_api, edit_withdraw_request_api } from '../controllers/dataController';
import Header from '../components/Header';
import { FaTimes, FaUniversity, FaArrowLeft, FaChevronDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/custom_ui/dropdown-menu.tsx";

const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'REJECTED'];

// Modal to display bank details
const BankDetailsModal = ({ request, onClose }) => {
    if (!request) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50" onClick={onClose} >
            <div className="bg-gradient-card border border-border/50 shadow-shadow-card rounded-lg p-8 max-w-md w-full shadow-xl">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">Bank Details for Request #{request.id}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white">
                        <FaTimes size={20} />
                    </button>
                </div>
                <div className="space-y-4 text-gray-300">
                    <p><strong>Artist:</strong> {request.artist_username}</p>
                    <p><strong>Account Holder:</strong> {request.account_holder_name}</p>
                    <p><strong>Account Number:</strong> {request.account_number}</p>
                    <p><strong>Bank Name:</strong> {request.bank_name}</p>
                    <p><strong>IFSC Code:</strong> {request.ifsc_code}</p>
                    <p><strong>Account Type:</strong> <span className="capitalize">{request.account_type?.toLowerCase()}</span></p>
                    <p><strong>Transaction ID:</strong> {request.transaction_id || 'N/A'}</p>
                </div>
            </div>
        </div>
    );
};

const getStatusClasses = (status) => {
    switch (status) {
        case 'APPROVED': return 'bg-green-600 text-white';
        case 'REJECTED': return 'bg-red-600 text-white';
        case 'PENDING': return 'bg-yellow-600 text-white';
        default: return 'bg-gray-500 text-white';
    }
};

const StatusDropdown = ({ currentStatus, requestId, onStatusChange }) => {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={`inline-flex justify-center items-center w-full rounded-md border border-gray-600 shadow-sm px-4 py-2 text-sm font-medium ${getStatusClasses(currentStatus)} transition-colors duration-150 outline-none`}
                >
                    {currentStatus}
                    <FaChevronDown className="ml-2 h-3 w-3" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-gray-800 border-gray-700 z-[100]">
                {STATUS_OPTIONS.map(status => (
                    <DropdownMenuItem
                        key={status}
                        onClick={() => onStatusChange(requestId, status)}
                        className={`cursor-pointer ${status === currentStatus ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-700'}`}
                        disabled={status === currentStatus}
                    >
                        {status}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};


const WithdrawRequestPage = () => {
    const [requests, setRequests] = useState([]);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);

    const fetchWithdrawalRequests = () => {
        get_all_withdraw_request_api().then(response => {
            setRequests(response.data);
        }).catch(error => {
            console.error("Failed to get withdrawal requests:", error);
        });
    };

    useEffect(() => {
        fetchWithdrawalRequests();
    }, []);

    const filteredRequests = useMemo(() => {
        if (filterStatus === 'ALL') return requests;
        return requests.filter(req => req.status === filterStatus);
    }, [requests, filterStatus]);

    const updateRequestStatus = (id, newStatus) => {
        edit_withdraw_request_api({ id, status: newStatus }).then(() => {
            fetchWithdrawalRequests();
        }).catch(error => {
            console.error("Failed to update withdrawal request:", error);
        });
    };
    
    const handleViewDetails = (request) => {
        setSelectedRequest(request);
        setShowBankDetailsModal(true);
    };

    const totalRequests = requests.length;
    const pendingCount = requests.filter(req => req.status === 'PENDING').length;
    
    const tableHeaders = ["ID", "Artist", "Amount", "Requested At", "Status", "Action", "Bank Details"];

    const filterButtons = [
        { label: `All (${totalRequests})`, status: 'ALL' },
        { label: `Pending (${pendingCount})`, status: 'PENDING' },
        { label: 'Approved', status: 'APPROVED' },
        { label: 'Rejected', status: 'REJECTED' },
    ];


    return (
        <div className='bg-background min-h-screen'>
             <div className='top-0 left-0 right-0 '>
                <Header />
            </div>
        <div className=" text-gray-100 mx-20 sm:px-8">
           
            <div className="flex items-center mb-6 pt-4">
                <Link to="/admin_dashboard" className="text-muted-foreground hover:text-primary mr-4">
                    <FaArrowLeft size={20} />
                </Link>
                <h1 className="text-3xl font-extrabold text-white">Withdrawal Requests Management</h1>
            </div>
            <p className="text-gray-400 pb-5 ">Review, filter, and process all artist payment requests.</p>
            
            <div className="bg-gradient-card border border-border/50 shadow-shadow-card flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 p-4 rounded-lg">
                 
                <div className="flex flex-wrap gap-3 mb-4 sm:mb-0">
                    {filterButtons.map(({ label, status }) => (
                        <button
                            key={status}
                            onClick={() => setFilterStatus(status)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filterStatus === status ? 'bg-indigo-600 text-white shadow-lg' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div className="text-sm text-gray-400">
                    Showing <span className="font-semibold text-white">{filteredRequests.length}</span> results.
                </div>
            </div>

            <div className="bg-gradient-card border border-border/50 shadow-shadow-card rounded-lg shadow-xl overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-gray-700">
                        <tr>
                            {tableHeaders.map(header => (
                                <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y bg-gradient-card border border-border/50 shadow-shadow-card">
                        {filteredRequests.length > 0 ? (
                            filteredRequests.map((request) => (
                                <tr key={request.id} className="hover:bg-gray-700 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-300">{request.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-400 hover:underline cursor-pointer">{request.artist_username}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-400">₹{parseFloat(request.amount).toFixed(2)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">{new Date(request.requested_at).toLocaleString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap"><span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClasses(request.status)}`}>{request.status}</span></td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <StatusDropdown currentStatus={request.status} requestId={request.id} onStatusChange={updateRequestStatus} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <button onClick={() => handleViewDetails(request)} className="text-indigo-400 hover:text-indigo-600 font-semibold flex items-center">
                                            <FaUniversity className="mr-2" /> View Details
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan={tableHeaders.length} className="px-6 py-12 text-center text-gray-500">No {filterStatus.toLowerCase()} requests found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            {filteredRequests.filter(req => req.rejection_reason).length > 0 && (
                <div className="mt-8">
                    <h3 className="text-xl font-semibold text-red-400 mb-4">Rejection Reasons</h3>
                    <div className="space-y-4">
                        {filteredRequests.filter(req => req.rejection_reason).map(req => (
                            <div key={`reason-${req.id}`} className="p-4 bg-red-900 bg-opacity-30 border border-red-700 rounded-lg">
                                <p className="font-medium text-red-300">Request #{req.id} ({req.artist_username}):</p>
                                <p className="text-sm text-gray-300">{req.rejection_reason}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
        
        {showBankDetailsModal && <BankDetailsModal request={selectedRequest} onClose={() => setShowBankDetailsModal(false)} />}
        </div>
    );
};

export default WithdrawRequestPage;
